import { useEffect } from 'react'

import {
  useViewingRoomStore,
  useSelectedMarkerStore,
  SelectedMarker,
  useSidebarStore,
  type TLandmarkMarker,
} from '@/entities/map'
import { eventManager, mapManager, useOverlay } from '@/shared/lib'

import {
  type LandmarkOverlay,
  type LandmarkOverlayProps,
  createLandmarkOverlay,
} from '.'

import { getContent } from './element'

export const useLandmarkOverlay = () => {
  const sidebarActions = useSidebarStore(state => state.actions)

  const isSelected = useSelectedMarkerStore(state => state.isSelected)
  const selectMarker = useSelectedMarkerStore(
    state => state.actions.selectMarker,
  )

  const resetViewingRoom = useViewingRoomStore(
    state => state.actions.resetViewingRoom,
  )

  const updateSelectedMarker = (marker?: SelectedMarker) => {
    mapManager.withMap(map => {
      const zoomLevel = map.getZoom()

      if (marker) {
        resetViewingRoom()
      }

      if (marker && marker.nextZoomLevel && marker.nextZoomLevel <= zoomLevel) {
        selectMarker(marker)
      }
    })
  }

  const updateSidebar = () => {
    const isSidebarOpen = useSidebarStore.getState().isOpen

    if (!isSidebarOpen) {
      sidebarActions.open()

      setTimeout(() => {
        eventManager.triggerEvent('resize')
      }, 0)
    }
  }

  const handleLandmarkOverlayClick = (marker?: SelectedMarker) => {
    updateSelectedMarker(marker)
    updateSidebar()
  }

  const landmarkOverlay = useOverlay<
    LandmarkOverlay,
    LandmarkOverlayProps,
    TLandmarkMarker
  >({
    createInstance: createLandmarkOverlay,
    getOverlayOptions: item => ({
      zIndex: 10,
      content: getContent(item.name),
      nextZoomLevel: item.nextZoomLevel,
      onClick: handleLandmarkOverlayClick,
    }),
  })

  const handleSelectedMarkerChange = () => {
    if (!landmarkOverlay.ref.current) return

    landmarkOverlay.ref.current.forEach(overlay => {
      if (overlay.isSelected && !isSelected(overlay.id)) {
        overlay.markAsDeselected()
      }
    })
  }

  useEffect(
    () =>
      useSelectedMarkerStore.subscribe(
        state => state.selectedMarker,
        handleSelectedMarkerChange,
      ),
    [],
  )

  return landmarkOverlay
}
